import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertCircle, CheckCircle, X, Sparkles, Users, TrendingUp, Info, Package, PhoneCall } from 'lucide-react';

const SERVICES = [
  { id: 'screen', name: 'Réparation écran', icon: '📱' },
  { id: 'battery', name: 'Batterie', icon: '🔋' },
  { id: 'diagnostic', name: 'Diagnostic gratuit', icon: '🔍' },
  { id: 'microsolder', name: 'Micro-soudure', icon: '⚡' },
  { id: 'other', name: 'Autre réparation', icon: '🔧' }
];

const DURATIONS = [
  { value: 0.25, label: '15 minutes', description: 'Interventions rapides' },
  { value: 0.5, label: '30 minutes', description: 'Réparations basiques' },
  { value: 1, label: '1 heure', description: 'Réparations standard' },
  { value: 1.5, label: '1h30', description: 'Réparations complexes' },
  { value: 2, label: '2 heures', description: 'Interventions avancées' },
  { value: 3, label: '3 heures ou plus', description: 'Réparations spécialisées' }
];

export const BookingCalendar = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: Date, 2: Créneau, 3: Form, 4: Confirmation Preview, 5: Success
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [smartSlots, setSmartSlots] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showDelayInfo, setShowDelayInfo] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    device: '',
    service: '',
    duration: 1,
    message: ''
  });

  // Générer les 14 prochains jours (excluant les dimanches)
  const getNext14Days = () => {
    const days = [];
    const today = new Date();
    let count = 0;
    let dayOffset = 0;
    
    while (count < 14) {
      const date = new Date(today);
      date.setDate(today.getDate() + dayOffset);
      
      // Exclure les dimanches (0)
      if (date.getDay() !== 0) {
        days.push({
          date: date.toISOString().split('T')[0],
          day: date.getDate(),
          month: date.toLocaleDateString('fr-FR', { month: 'short' }),
          dayName: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
          isToday: dayOffset === 0
        });
        count++;
      }
      dayOffset++;
    }
    return days;
  };

  const [availableDays] = useState(getNext14Days());

  // Charger les créneaux quand une date est sélectionnée
  useEffect(() => {
    if (selectedDate) {
      loadSlots(selectedDate);
    }
  }, [selectedDate]);

  const loadSlots = async (date) => {
    setLoading(true);
    setError('');
    
    try {
      const backendUrl = window.location.origin;
      const response = await fetch(`${backendUrl}/api/appointments/available-slots?date=${date}`);
      
      if (!response.ok) throw new Error('Erreur chargement créneaux');
      
      const data = await response.json();
      setSmartSlots(data.smart_slots || []);
      setStats(data.stats);
    } catch (err) {
      setError('Impossible de charger les créneaux disponibles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep(2);
  };

  const handleSelectSlot = (slot) => {
    if (!slot.available) return;
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleServiceChange = (serviceId) => {
    setFormData({
      ...formData,
      service: serviceId
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.device || !formData.service) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    // Afficher le modal de confirmation au lieu de soumettre directement
    setShowConfirmation(true);
  };
  
  const confirmAndSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      const backendUrl = window.location.origin;
      
      const appointmentData = {
        ...formData,
        date: selectedDate,
        time: selectedSlot.time
      };
      
      const response = await fetch(`${backendUrl}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointmentData)
      });
      
      if (!response.ok) throw new Error('Erreur lors de la réservation');
      
      setShowConfirmation(false);
      setSuccess(true);
      setStep(5);
      
      // Réinitialiser après 5 secondes
      setTimeout(() => {
        onClose();
        resetForm();
      }, 5000);
      
    } catch (err) {
      setError('Impossible de créer le rendez-vous. Veuillez réessayer.');
      setShowConfirmation(false);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedDate('');
    setSelectedSlot(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      device: '',
      service: '',
      duration: 1,
      message: ''
    });
    setSuccess(false);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  // Obtenir le badge prioritaire basé sur le type de créneau
  const getPriorityBadge = (type, priority) => {
    if (priority === 'premium') return { color: 'from-red-500 to-pink-500', text: '🔥 PREMIUM' };
    if (priority === 'high') return { color: 'from-orange-500 to-yellow-500', text: '⚡ POPULAIRE' };
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto relative">
        
        {/* Bouton fermeture */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-3xl">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 p-3 rounded-full">
              <Calendar size={32} />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">Réservation Prioritaire</h2>
              <p className="text-blue-100 text-sm">Créneaux intelligents - Rush hours optimisés</p>
            </div>
          </div>
        </div>

        {/* Contenu */}
        <div className="p-6">
          
          {/* Indicateur d'étapes */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            {[
              { num: 1, label: 'Date' },
              { num: 2, label: 'Créneau' },
              { num: 3, label: 'Infos' },
              { num: 4, label: 'Confirmation' }
            ].map((s) => (
              <div key={s.num} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s.num}
                </div>
                <span className={`ml-2 text-sm font-medium hidden md:block ${
                  step >= s.num ? 'text-blue-600' : 'text-gray-400'
                }`}>
                  {s.label}
                </span>
                {s.num < 4 && <div className="w-8 h-0.5 bg-gray-300 mx-2" />}
              </div>
            ))}
          </div>

          {error && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6 flex items-center space-x-3">
              <AlertCircle className="text-red-500" size={24} />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* ÉTAPE 1 : Sélection de la date */}
          {step === 1 && (
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center space-x-2">
                <Calendar className="text-blue-600" size={24} />
                <span>Choisissez votre date</span>
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {availableDays.map((day) => (
                  <button
                    key={day.date}
                    onClick={() => handleSelectDate(day.date)}
                    className={`p-4 rounded-xl border-2 transition-all hover:scale-105 ${
                      selectedDate === day.date
                        ? 'border-blue-600 bg-blue-50 shadow-lg'
                        : 'border-gray-200 hover:border-blue-300'
                    } ${day.isToday ? 'ring-2 ring-orange-400' : ''}`}
                  >
                    <div className="text-center">
                      <div className="text-xs text-gray-500 uppercase">{day.dayName}</div>
                      <div className="text-2xl font-bold my-1">{day.day}</div>
                      <div className="text-xs text-gray-600">{day.month}</div>
                      {day.isToday && (
                        <div className="text-xs text-orange-500 font-bold mt-1">Aujourd'hui</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ÉTAPE 2 : Sélection du créneau */}
          {step === 2 && (
            <div>
              <button
                onClick={() => setStep(1)}
                className="text-blue-600 hover:underline mb-4 flex items-center space-x-1"
              >
                <span>←</span>
                <span>Changer de date</span>
              </button>

              <h3 className="text-xl font-bold mb-2 flex items-center space-x-2">
                <Clock className="text-blue-600" size={24} />
                <span>Créneaux intelligents disponibles</span>
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                📅 {new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>

              {/* Stats en temps réel */}
              {stats && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="text-2xl font-bold text-green-600">{stats.available}</div>
                    <div className="text-xs text-gray-600">Disponibles</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl">
                    <div className="text-2xl font-bold text-orange-600">{stats.occupied}</div>
                    <div className="text-xs text-gray-600">Réservés</div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <div className="text-2xl font-bold text-blue-600">{stats.occupancy_rate}%</div>
                    <div className="text-xs text-gray-600">Taux d'occupation</div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto" />
                  <p className="mt-4 text-gray-600">Chargement des créneaux...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {smartSlots.map((slot) => {
                    const badge = getPriorityBadge(slot.type, slot.priority);
                    
                    return (
                      <button
                        key={slot.time}
                        onClick={() => handleSelectSlot(slot)}
                        disabled={!slot.available}
                        className={`p-4 rounded-xl border-2 transition-all text-left relative ${
                          !slot.available
                            ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                            : selectedSlot?.time === slot.time
                            ? 'border-blue-600 bg-blue-50 shadow-lg scale-105'
                            : 'border-gray-300 hover:border-blue-400 hover:scale-105 hover:shadow-md'
                        }`}
                      >
                        {badge && slot.available && (
                          <div className={`absolute -top-2 -right-2 bg-gradient-to-r ${badge.color} text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg`}>
                            {badge.text}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-2xl font-bold">{slot.time}</div>
                          {!slot.available && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                              Complet
                            </span>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-700 font-medium mb-1">{slot.label}</div>
                        
                        {slot.recommended && slot.available && (
                          <div className="flex items-center space-x-1 text-xs text-green-600 font-medium">
                            <Sparkles size={14} />
                            <span>Recommandé</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ÉTAPE 3 : Formulaire */}
          {step === 3 && (
            <div>
              <button
                onClick={() => setStep(2)}
                className="text-blue-600 hover:underline mb-4 flex items-center space-x-1"
              >
                <span>←</span>
                <span>Changer de créneau</span>
              </button>

              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-xl mb-6 border-2 border-orange-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-orange-500 text-white p-3 rounded-full">
                    <CheckCircle size={24} />
                  </div>
                  <div>
                    <div className="font-bold text-lg">🔥 Prise en charge PRIORITAIRE</div>
                    <div className="text-sm text-gray-700">
                      {new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {selectedSlot.time}
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Nom complet *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-600 focus:outline-none"
                    placeholder="Jean Dupont"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-600 focus:outline-none"
                      placeholder="jean@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-600 focus:outline-none"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Type d'appareil *</label>
                  <input
                    type="text"
                    required
                    value={formData.device}
                    onChange={(e) => setFormData({ ...formData, device: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-600 focus:outline-none"
                    placeholder="iPhone 13, Samsung Galaxy S22..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Type de réparation *</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {SERVICES.map((service) => (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => handleServiceChange(service.id)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.service === service.id
                            ? 'border-blue-600 bg-blue-50 shadow-lg'
                            : 'border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        <div className="text-3xl mb-2">{service.icon}</div>
                        <div className="text-sm font-medium">{service.name}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sélecteur de durée */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold">Durée estimée *</label>
                    <button
                      type="button"
                      onClick={() => setShowDelayInfo(!showDelayInfo)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
                    >
                      <Info size={16} />
                      <span>En savoir plus sur les délais</span>
                    </button>
                  </div>
                  
                  {/* Info sur les délais */}
                  {showDelayInfo && (
                    <div className="mb-4 bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-3">
                      <h4 className="font-bold text-blue-900 flex items-center space-x-2">
                        <Clock className="text-blue-600" size={20} />
                        <span>Variété des temps d'intervention</span>
                      </h4>
                      
                      <div className="space-y-2 text-sm text-gray-700">
                        <p className="flex items-start space-x-2">
                          <span className="text-green-600 font-bold mt-0.5">✓</span>
                          <span><strong>Réparations basiques (15min - 1h)</strong> : Écran, batterie, vitre tactile</span>
                        </p>
                        
                        <p className="flex items-start space-x-2">
                          <span className="text-orange-600 font-bold mt-0.5">⚡</span>
                          <span><strong>Réparations complexes (1h - 2h)</strong> : Micro-soudure, carte mère, connecteurs</span>
                        </p>
                        
                        <p className="flex items-start space-x-2">
                          <span className="text-blue-600 font-bold mt-0.5">⏰</span>
                          <span><strong>Interventions spécialisées (2h+)</strong> : Dépend de la disponibilité des pièces et de la complexité</span>
                        </p>
                      </div>
                      
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                        <div className="flex items-start space-x-2">
                          <Package className="text-yellow-600 mt-0.5" size={18} />
                          <div className="text-sm">
                            <p className="font-bold text-yellow-900 mb-1">📦 Commande de pièces</p>
                            <p className="text-gray-700">
                              Si votre pièce n'est pas en stock, nous la commandons sur demande.
                              <br />
                              <strong className="text-yellow-800">⏰ Horaires recommandés pour commande : 9h - 12h</strong>
                              <br />
                              <span className="text-xs">Livraison généralement sous 24-48h</span>
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded">
                        <div className="flex items-start space-x-2">
                          <PhoneCall className="text-green-600 mt-0.5" size={18} />
                          <div className="text-sm">
                            <p className="font-bold text-green-900 mb-1">💬 Besoin d'aide ?</p>
                            <p className="text-gray-700">
                              Appelez-nous au <strong className="text-green-800">04 26 00 82 10</strong> pour un conseil personnalisé sur la durée de votre intervention.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {DURATIONS.map((dur) => (
                      <button
                        key={dur.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, duration: dur.value })}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          formData.duration === dur.value
                            ? 'border-blue-600 bg-blue-50 shadow-lg'
                            : 'border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        <div className="text-lg font-bold">{dur.label}</div>
                        <div className="text-xs text-gray-500 mt-1">{dur.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Message (optionnel)</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-600 focus:outline-none"
                    rows="3"
                    placeholder="Précisions sur votre panne..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !formData.service}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {loading ? 'Réservation en cours...' : '🔥 Confirmer mon RDV Prioritaire'}
                </button>
              </form>
            </div>
          )}

          {/* ÉTAPE 4 : Confirmation */}
          {step === 5 && success && (
            <div className="text-center py-8">
              <div className="bg-green-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-600" size={48} />
              </div>
              
              <h3 className="text-3xl font-bold text-green-600 mb-4">
                ✅ Rendez-vous Confirmé !
              </h3>
              
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-xl mb-6 max-w-md mx-auto border-2 border-orange-200">
                <div className="text-2xl font-bold mb-2">🔥 PRISE EN CHARGE PRIORITAIRE</div>
                <div className="text-gray-700">
                  <div className="font-bold text-xl my-2">
                    {new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </div>
                  <div className="text-3xl font-bold text-blue-600 my-2">{selectedSlot.time}</div>
                </div>
              </div>
              
              <p className="text-gray-600 mb-2">
                📧 Vous allez recevoir un email de confirmation à <strong>{formData.email}</strong>
              </p>
              
              <p className="text-sm text-gray-500 mb-6">
                En cas d'empêchement, appelez-nous au 04 26 00 82 10
              </p>
              
              <div className="text-xs text-gray-400">
                Cette fenêtre se fermera automatiquement...
              </div>
            </div>
          )}

        </div>
      </div>
      
      {/* Modal de Confirmation Avant Validation */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-blue-600 mb-6 flex items-center space-x-2">
                <AlertCircle size={28} />
                <span>✅ Vérifiez votre réservation</span>
              </h3>
              
              {/* Résumé du RDV */}
              <div className="bg-blue-50 p-6 rounded-xl mb-6">
                <h4 className="font-bold text-lg mb-4">📅 Récapitulatif de votre rendez-vous</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-bold">{new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Heure</p>
                    <p className="font-bold">{selectedSlot?.time}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Service</p>
                    <p className="font-bold capitalize">{SERVICES.find(s => s.id === formData.service)?.name || formData.service}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Durée estimée</p>
                    <p className="font-bold">{DURATIONS.find(d => d.value === formData.duration)?.label || `${formData.duration}h`}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-600">Appareil</p>
                  <p className="font-bold">{formData.device}</p>
                </div>
                {formData.message && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-600">Message</p>
                    <p className="font-bold">{formData.message}</p>
                  </div>
                )}
              </div>
              
              {/* Informations sur les délais */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-6">
                <h4 className="font-bold text-yellow-900 mb-3 flex items-center space-x-2">
                  <Clock size={20} />
                  <span>⏰ Informations importantes sur les délais</span>
                </h4>
                
                <div className="space-y-2 text-sm text-gray-700">
                  <p className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span><strong>Réparations basiques (15min - 1h)</strong> : Écran, batterie, vitre tactile</span>
                  </p>
                  
                  <p className="flex items-start space-x-2">
                    <span className="text-orange-600 font-bold mt-0.5">⚡</span>
                    <span><strong>Réparations complexes (1h - 2h)</strong> : Micro-soudure, carte mère, connecteurs</span>
                  </p>
                  
                  <p className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold mt-0.5">⏰</span>
                    <span><strong>Interventions spécialisées (2h+)</strong> : Dépend de la disponibilité des pièces</span>
                  </p>
                </div>
              </div>
              
              {/* Commande de pièces */}
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded mb-6">
                <h4 className="font-bold text-orange-900 mb-2 flex items-center space-x-2">
                  <Package size={20} />
                  <span>📦 Besoin d'une pièce spécifique ?</span>
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Si votre pièce n'est pas en stock, nous pouvons la commander pour vous.
                </p>
                <p className="text-sm font-bold text-orange-800">
                  ⏰ <strong>Horaires recommandés pour commander : 9h - 12h</strong>
                </p>
                <p className="text-xs text-gray-600 mt-1">Livraison généralement sous 24-48h</p>
              </div>
              
              {/* Contact */}
              <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded mb-6">
                <h4 className="font-bold text-green-900 mb-2">💬 Une question ?</h4>
                <p className="text-sm text-gray-700">
                  Appelez-nous au <strong className="text-green-800">04 26 00 82 10</strong> pour un conseil personnalisé.
                </p>
              </div>
              
              {/* Boutons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-4 rounded-xl transition-colors flex items-center justify-center space-x-2"
                  disabled={loading}
                >
                  <span>← Modifier</span>
                </button>
                <button
                  onClick={confirmAndSubmit}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <span>Réservation...</span>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      <span>✅ Confirmer et Réserver</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
